import dayjs from 'dayjs';
import { Subscription } from '@demind-inc/core';
import Stripe from 'stripe';

/*
  @example: currency: USD , price: 3.00 -> $3.00
*/
export const formatCurrencyPrice = (currency: string, price: number) => {
  return new Intl.NumberFormat('en-EN', { style: 'currency', currency }).format(price);
};

export const translatePlanPriceDetail = (plan: Stripe.Price, onlyPrice = false): string => {
  const recurring = plan.recurring?.interval;
  const price = plan.unit_amount / 100; // Dollar
  const currency = plan.currency;

  if (onlyPrice) {
    return formatCurrencyPrice(currency, price);
  }

  if (recurring === 'year') {
    return formatCurrencyPrice(currency, price);
  }

  return formatCurrencyPrice(currency, price);
};

export const translateLifetimePlanPriceDetail = (plan: Stripe.Price): string => {
  const price = plan.unit_amount / 100; // Dollar
  const currency = plan.currency;

  return formatCurrencyPrice(currency, price);
};

const LIFETIME_PLAN_EMAIL_RECIPIENT = 'contact@demind-inc.com';
const LIFETIME_PLAN_PRICE_FALLBACK = '$120';

export const getLifetimePlanEmailLink = (
  lifetimePlanPrice: string | undefined,
  userEmail: string
): { subject: string; priceLabel: string; body: string; mailto: string } => {
  const subject = 'Lifetime plan request';
  const priceLabel = lifetimePlanPrice ?? LIFETIME_PLAN_PRICE_FALLBACK;
  const body = `Hi DeMind team,\n\nI'd like to switch to the Lifetime plan.\n\nThank you.\n\n--\nUser email: ${userEmail}`;
  const mailto = `mailto:${LIFETIME_PLAN_EMAIL_RECIPIENT}?subject=${encodeURIComponent(
    subject
  )}&body=${encodeURIComponent(body)}`;
  return { subject, priceLabel, body, mailto };
};

function checkFreeTrial(subscription: Subscription): boolean {
  if (!subscription.freeTrial) {
    return false;
  }

  return dayjs().isBefore(dayjs(subscription.freeTrial.endDate));
}

function getFormattedSubscriptionDates(subscription: Subscription) {
  return {
    startFreeTrialDate: subscription.freeTrial
      ? dayjs(subscription.freeTrial.startDate).format('YYYY-MM-DD')
      : undefined,
    startChargeDate: subscription.freeTrial
      ? dayjs(subscription.freeTrial.endDate).format('YYYY-MM-DD')
      : dayjs().format('YYYY-MM-DD'),
    expirationDate: subscription?.expirationDate
      ? dayjs(subscription.expirationDate).format('YYYY-MM-DD')
      : undefined,
    unsubscribedDate: subscription?.unsubscribedDate
      ? dayjs(subscription.unsubscribedDate).format('YYYY-MM-DD')
      : undefined,
  };
}

function getFormattedPrice(subscription: Subscription): string {
  if (!subscription.price) {
    return '';
  }
  return formatCurrencyPrice(subscription.price.currency, subscription.price.amount);
}

function getPriceAfterTrial(
  subscription: Subscription,
  price: string,
  expiredInsteadOfCharge: boolean = false
): string {
  const priceMapping: Record<Subscription['status'], string> = {
    freeTrial: price || (expiredInsteadOfCharge ? 'Expired (No auto-charge)' : 'Start Charging'),
    unsubscribed: 'Unsubscribed',
    active: '',
    expired: '',
  };
  return priceMapping[subscription.status];
}

function getPriceWithRecurring(subscription: Subscription, price: string): string {
  const priceMapping: Record<Subscription['recurring'], string> = {
    ANNUAL: `${price} / Year`,
    MONTHLY: `${price} / Month`,
    WEEKLY: `${price} / Week`,
    LIFETIME: `${price} (One-time payment)`,
  };
  return priceMapping[subscription.recurring];
}

export {
  checkFreeTrial,
  getFormattedSubscriptionDates,
  getFormattedPrice,
  getPriceAfterTrial,
  getPriceWithRecurring,
};
